import { z } from 'zod';
import { ROLES } from '@bekebi/domain';
import { uuidSchema } from '../common';
import { pageSchema } from './pagination';

/**
 * Portal users (permission `users.manage`, Admin only). The Bekebi User ID is generated by the
 * system and never accepted in a request. Bodies are strict. An Admin never sets or sees another
 * user's password: a new user receives an invitation link and chooses it (DECISIONS.md A-AC1).
 */
export const userCreateSchema = z.strictObject({
  email: z.string().trim().email().max(254),
  role: z.enum(ROLES),
});
export type UserCreate = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = z
  .strictObject({
    role: z.enum(ROLES).optional(),
    status: z.enum(['active', 'disabled']).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'no fields to update' });
export type UserUpdate = z.infer<typeof userUpdateSchema>;

export const userParamsSchema = z.object({ userId: uuidSchema });

export const userSchema = z.object({
  id: uuidSchema,
  bekebiUserId: z.string(),
  email: z.string(),
  role: z.enum(ROLES),
  status: z.enum(['active', 'disabled']),
  /** False while the user has not chosen a password: invited, or after an Admin reset. */
  passwordSet: z.boolean(),
  /**
   * The user's newest invitation or reset link: whether it is still open, and until when. Never
   * the link or its token.
   */
  accountLink: z
    .object({
      purpose: z.enum(['invitation', 'password_reset']),
      state: z.enum(['open', 'used', 'expired', 'replaced']),
      expiresAt: z.string(),
    })
    .nullable(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type User = z.infer<typeof userSchema>;

export const userPageSchema = pageSchema(userSchema);

/**
 * What happened to the email carrying a new account link. `setupLink` is returned only when
 * the email was not accepted by the mail server (or email is not set up), so the Admin can pass
 * the link on another way; it is shown once and never stored or returned again.
 */
export const accountLinkIssueSchema = z.object({
  purpose: z.enum(['invitation', 'password_reset']),
  expiresAt: z.string(),
  email: z.enum(['accepted', 'failed', 'not_set_up']),
  setupLink: z.string().nullable(),
});
export type AccountLinkIssue = z.infer<typeof accountLinkIssueSchema>;

export const userCreateResultSchema = z.object({
  user: userSchema,
  invitation: accountLinkIssueSchema,
});
export type UserCreateResult = z.infer<typeof userCreateResultSchema>;
